import { useEffect, useRef, useState } from '@wordpress/element';
import ApexCharts from 'apexcharts';

import { ordersChartFilters } from '../../includes/options';
import { useDashboard } from '../../context/DashboardContext';

export default function OrderChart({ loadingState }) {

    const chartRef          = useRef(null);
    const chartInstanceRef  = useRef(null);
    const [chartLoadingState, setChartLoadingState]  = useState(false);
    
    const [ orderChartFilter, setOrderChartFilter ] = useState('group-users');

    const { data, fetchOrderChartData, filters, fetchDashboardData, setCurrentChartFilter } = useDashboard();

    const handleOrdersChartFilter = key => {
        setOrderChartFilter(key);
        setCurrentChartFilter(key);
        fetchOrderChartData(key, setChartLoadingState);
    };

    // Respond to parent filter changes - refetch dashboard data with current chart filter
    // Use a ref to track previous filters to prevent unnecessary calls
    const prevFiltersRef = useRef('');
    
    useEffect(() => {
        // Only refetch if filters actually changed and we're not already loading
        if (!loadingState && orderChartFilter && filters && prevFiltersRef.current !== filters) {
            prevFiltersRef.current = filters;
            fetchDashboardData(filters, orderChartFilter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, loadingState]);
    useEffect(() => {
        if (loadingState || chartLoadingState || !chartRef.current || typeof ApexCharts === 'undefined') {
            return;
        }
        const options = {
            chart: {
                type: 'area',
                zoom: {
                    enabled: false
                },
                height: 320,
                toolbar: { show: false },
                fontFamily: "Poppins, sans-serif",
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.55,
                    opacityTo: 0,
                    shade: "#686a6e",
                    gradientToColors: ["#686a6e"],
                },
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'straight',
                width: 2,
            },
            grid: {
                show: true,
                strokeDashArray: 1,
                padding: {
                    left: 12,
                    right: 2,
                    top: 0
                },
            },
            series: [
                {
                    name: ordersChartFilters.find(item => item.key === orderChartFilter)?.label,
                    data: data?.chart?.data,
                    color: "#686a6e",
                },
            ],
            xaxis: {
                categories: data?.chart?.series,
                labels: {
                    show: true,
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: true,
                },
            },
            yaxis: {
                show: true,
                tickAmount: 5,
                forceNiceScale: true,
                labels: {
                    formatter: (val) => Math.round(val)
                }
            },
            tooltip: {
                y: {
                  formatter: function (val) {
                    return parseFloat(val); 
                  }
                }
              }
        };

        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        const chart = new ApexCharts(chartRef.current, options);
        chartInstanceRef.current = chart;
        chart.render();

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [loadingState, chartLoadingState, data, orderChartFilter]);

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 order-chart">
            <div className="flex justify-between mb-3">
                <div className="inline-flex rounded-md shadow-xs filter-buttons" role="group">
                    { ordersChartFilters.length && ordersChartFilters.map((element, index) => (
                        <button
                            type="button"
                            className={`px-4 py-2 text-sm font-medium text-gray-900 bg-white border-gray-200 hover:bg-gray-100 ${ ( 0 === index || ordersChartFilters.length-1 === index ) ? `border rounded-${ordersChartFilters.length-1 === index ? 'r' : 'l'}-lg` : 'border-t border-b cs-border-r-wd' } ${ ( element.key === orderChartFilter ) ? 'active' : null }`}
                            data-key={element.key}
                            onClick={e => handleOrdersChartFilter(e.target.dataset.key)}
                        >
                        { element.label }
                    </button>
                    )) }
                </div>
            </div>
            {loadingState || chartLoadingState ? <Skeleton /> : <div ref={chartRef}></div> }
        </div>
    );
}

function Skeleton() {
    return (
        <div role="status" class="relative bg-white rounded-lg shadow-sm p-4 animate-pulse" style={{ height: '290px', width: '97%', marginLeft: 'auto' }}>
            <div class="absolute inset-0 flex flex-col justify-between">
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
                <div class="w-full h-px bg-gray-100"></div>
            </div>
            <div class="absolute top-0 flex flex-col justify-between h-full py-2 z-10" style={{width: '16px', left: '-22px'}}>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
                <div class="w-1 h-3 bg-gray-300 rounded"></div>
            </div>
            <div class="absolute bottom-0 left-0 w-full h-auto">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0.267 66.756 399.733 133.244" preserveAspectRatio="none" style={{ width: '100%', height: '280px' }}>
                    <path d="M 0 150 L 52.34 128.773 L 92.104 108.652 L 156.141 80.934 L 200 110 C 252.276 140.561 265.8 147.266 265.792 147.261 L 316.962 101.17 L 400.2 66.796 L 400 200 L 0 200 L 0 150 Z" class="fill-gray-200"/>
                </svg>
            </div>

            <div class="absolute left-0 flex justify-between w-full px-2" style={{ bottom: '-22px' }}>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
                <div class="w-3 h-1 bg-gray-300 rounded"></div>
            </div>

            <span class="sr-only">Loading area chart...</span>
        </div>
    );
}